<template>
    <div>
        <p class="text-center" v-if="message.mainDate">
            {{ message.mainDate }}
        </p>
        <div
            class="message-bubble"
            :id="message.sid"
            :class="direction"
        >
            <div
                class="message-bubble__body"
                :class="{'has-reply': repliedToMessage !== null}"
            >
                <div class="message-body__reply" v-if="repliedToMessage">
                    <div class="reply-box">
                        <div class="reply-user-name">
                            <strong><small>{{ handleTwilioUserNameOf(repliedToMessageUser) }}</small></strong>
                        </div>
                        <template v-if="isDeletedRepliedToMessage">
                            Deleted message
                        </template>
                        <template v-else>
                            <span style="white-space: pre-wrap">{{ repliedToMessage.body }}</span>
                            <div v-if="repliedToMessageHasTwilioMedia" class="message-media">
                                <img :src="repliedToMessageTwilioMediaUrl"/>
                            </div>
                            <template v-else-if="repliedToMessageHasLocalMedia">
                                <div v-if="repliedToMessage.attributes.media.is_image" class="message-media">
                                    <div v-if="direction === 'incoming'" class="icon-media d-flex align-items-center">
                                        <img src="/img/icons/media.svg" alt="media"> Photo
                                    </div>
                                    <div v-else class="icon-media d-flex align-items-center">
                                        <img src="/img/icons/media-white.svg" alt="media"> Photo
                                    </div>
                                    <img class="media-reply" :src="repliedToMessage.attributes.media.urls.thumb"/>
                                </div>
                                <div v-else-if="repliedToMessage.attributes.media.is_video" class="message-media">
                                    <div v-if="direction === 'incoming'" class="icon-media d-flex align-items-center">
                                        <img src="/img/icons/video.svg" alt="video"> Video
                                    </div>
                                    <div v-else class="icon-media d-flex align-items-center">
                                        <img src="/img/icons/video-white.svg" alt="video"> Video
                                    </div>
                                    <img class="media-reply video-reply" :src="repliedToMessage.attributes.media.urls.thumb"/>
                                </div>
                                <div v-else-if="repliedToMessage.attributes.media.is_audio" class="audio-media">
                                    <div v-if="direction === 'incoming'" class="icon-media d-flex align-items-center">
                                        <img src="/img/icons/audio.svg" alt="audio"> Audio
                                    </div>
                                    <div v-else class="icon-media d-flex align-items-center audio">
                                        <img src="/img/icons/audio-white.svg" alt="audio"> Audio
                                    </div>
                                    <div class="is-audio">
                                        <audio controls :src="repliedToMessage.attributes.media.source[0].src">
                                            <a :href="repliedToMessage.attributes.media.source[0].src" target="_blank">
                                                Download audio
                                            </a>
                                        </audio>
                                    </div>
                                </div>
                                <div v-else>
                                    {{ repliedToMessage.attributes.media.file_name }}
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
                <div
                    class="message-bubble__text"
                    :class="{
                        'has-media': (messageHasTwilioMedia || messageHasLocalMedia) && !isDeletedMessage,
                        'missed-call': isMissedCallMessage
                    }"
                v-if="messageUser"
                >
                    <div class="message-bubble__username" v-if="direction === 'incoming' && !isMissedCallMessage">
                        <strong><small>{{ handleTwilioUserNameOf(messageUser) }}</small></strong>
                    </div>
                    <template v-if="isDeletedMessage">
                        <p>Deleted message</p>
                    </template>
                    <a
                        class="d-flex flex-row gap-2 align-items-center"
                        :class="{ 'me-5': direction === 'outgoing' }"
                        :href="`tel:${ primaryPhoneNumberE164 }`"
                        v-else-if="isMissedCallMessage"
                        @click="retryMissedCall">
                        <div class="missed-call-image">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56">
                                <path fill="red" d="M32.523 25.387h13.618c.96 0 1.71-.774 1.664-1.735c-.047-.914-.75-1.593-1.664-1.593h-3.563l-6.89.375l3.937-3.586L49.727 8.723c.375-.375.562-.797.562-1.29c0-.913-.774-1.64-1.758-1.64c-.469 0-.867.164-1.219.516L37.234 16.434l-3.61 3.89l.4-6.867V9.965c0-.914-.704-1.617-1.618-1.664a1.657 1.657 0 0 0-1.734 1.664v13.57c0 1.242.61 1.852 1.851 1.852m-14.93 12.82c6.774 6.773 15.024 12 21.75 12c3.024 0 5.672-1.055 7.805-3.398c1.242-1.383 2.016-3 2.016-4.594c0-1.172-.445-2.297-1.57-3.094l-7.172-5.11c-1.102-.75-2.016-1.124-2.86-1.124c-1.078 0-2.015.61-3.093 1.664l-1.664 1.64a1.263 1.263 0 0 1-.891.375c-.375 0-.703-.14-.961-.257c-1.43-.774-3.914-2.907-6.234-5.203c-2.297-2.297-4.43-4.782-5.18-6.235a1.99 1.99 0 0 1-.258-.937c0-.305.094-.61.352-.868l1.64-1.71c1.055-1.079 1.664-2.016 1.664-3.094c0-.844-.375-1.758-1.148-2.86l-5.04-7.101c-.82-1.125-1.968-1.617-3.233-1.617c-1.547 0-3.165.703-4.524 2.039c-2.273 2.18-3.281 4.875-3.281 7.851c0 6.727 5.133 14.883 11.883 21.633"/>
                            </svg>
                        </div>
                        <div class="missed-call-text">
                            <strong>{{ direction === 'incoming' ? 'Missed voice call' : 'No answer' }} </strong>
                            <p class="mb-0">Press to call {{ direction === 'incoming' ? 'back' : 'again' }}</p>
                        </div>
                    </a>
                    <template v-else>
                        <p style="white-space: pre-wrap">{{ message.body }}</p>
                        <template v-if="messageHasLocalMedia">
                            <div
                                v-if="message.attributes.media.is_image || message.attributes.media.is_video"
                                class="message-media js-gallery-img"
                                @click="$emit('open-gallery', message.sid)"
                            >
                                <img :src="message.attributes.media.urls.thumb"/>
                            </div>
                            <div v-else-if="message.attributes.media.is_audio" class="has-audio">
                                <audio controls :src="message.attributes.media.source[0].src">
                                    <a :href="message.attributes.media.source[0].src" target="_blank">
                                        Download audio
                                    </a>
                                </audio>
                            </div>
                            <div v-else>
                                {{ message.attributes.media.file_name }}
                            </div>
                        </template>
                        <div
                            v-if="messageHasTwilioMedia"
                            class="message-media js-gallery-img"
                            @click="$emit('open-gallery', message.sid)"
                        >
                            <img :src="messageTwilioMediaUrl"/>
                        </div>
                        <div class="reply-btn" :class="direction">
                            <a href="#" @click.prevent="$emit('reply-to', message)">
                                <img src="/img/icons/reply.svg" alt="reply">
                            </a>
                        </div>
                        <div v-if="direction === 'outgoing'" class="delete-btn">
                            <a href="#" @click.prevent="deleteMessage(message)">
                                <img src="/img/icons/delete.png" width="12" height="12" alt="delete">
                            </a>
                        </div>
                    </template>
                    <span class="message-bubble__meta">
                        {{ messageDate }}
                        <template v-if="direction === 'outgoing'">
                            <span v-if="isRead">
                                <img src="/img/icons/read-check.svg" alt="read check">
                            </span>
                            <span v-else> <img src="/img/icons/delivered-check.svg" alt="delivered check"></span>
                        </template>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { handleTwilioUserNameOf, scrollToLastMessage } from '@helpers/helpers';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';

export default {
    props: {
        conversationsClient: {
            type: Object,
            required: true,
            default: null,
        },
        conversation: {
            required: true,
        },
        participants: {
            type: Array,
            required: true,
        },
        message: {
            required: true,
        },
        direction: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            messageUser: null,
            messageHasTwilioMedia: this.message.type === 'media',
            messageHasLocalMedia: this.message.attributes.hasOwnProperty('media'),
            messageTwilioMediaUrl: null,
            isLocalDeletedMessage: this.message.attributes.is_deleted,
            repliedToMessage: null,
            repliedToMessageUser: null,
            repliedToMessageHasTwilioMedia: false,
            repliedToMessageHasLocalMedia: false,
            repliedToMessageTwilioMediaUrl: null,
        };
    },
    async mounted() {
        this.messageUser = await this.conversationsClient.getUser(this.message.author);

        if (this.message.attributes && this.message.attributes.hasOwnProperty('replyToIndex')) {
            const m = await this.conversation.getMessages(1, this.message.attributes.replyToIndex);
            const message = m.items.shift();
            this.repliedToMessageUser = await this.conversationsClient.getUser(message.author);
            this.repliedToMessage = message;
            this.repliedToMessageHasTwilioMedia = this.repliedToMessage.type === 'media';
            this.repliedToMessageHasLocalMedia = this.repliedToMessage.attributes.hasOwnProperty('media');

            if (this.repliedToMessageHasTwilioMedia) {
                this.repliedToMessage.media
                    .getContentTemporaryUrl()
                    .then((url) => {
                        this.repliedToMessageTwilioMediaUrl = url;
                    });
            }
        }
        if (this.messageHasTwilioMedia) {
            this.message.media.getContentTemporaryUrl()
                .then((url) => {
                    this.messageTwilioMediaUrl = url;
                    this.$emit('add-dynamic-gallery-el', {
                        sid: this.message.sid,
                        src: url,
                        thumb: url,
                    });
                });
        }
        if (this.messageHasLocalMedia) {
            if (this.message.attributes.media.is_image) {
                this.$emit('add-dynamic-gallery-el', {
                    sid: this.message.sid,
                    src: this.message.attributes.media.urls.large,
                    thumb: this.message.attributes.media.urls.thumb,
                });
            }
            if (this.message.attributes.media.is_video) {
                this.$emit('add-dynamic-gallery-el', {
                    sid: this.message.sid,
                    thumb: this.message.attributes.media.urls.thumb,
                    video: {
                        source: this.message.attributes.media.source,
                        attributes: { preload: false, playsinline: true, controls: true },
                    },
                });
            }
        }
        setTimeout(
            this.scrollToLastMessage(
                document.getElementById(this.message.sid),
                document.querySelector('.js-scroller'),
            ),
            300,
        );
    },
    computed: {
        ...mapState('adDetails', ['primaryPhoneNumberE164']),
        messageDate() {
            const messageDate = moment(this.message.state.timestamp);
            return messageDate.format('HH:mm');
        },
        isRead() {
            return this.participants.some((participant) => this.messageUser
                && participant.identity !== this.messageUser.identity
                && participant.lastReadMessageIndex !== null
                && this.message.index <= participant.lastReadMessageIndex);
        },
        isDeletedMessage() {
            return this.message.attributes.is_deleted === true || this.isLocalDeletedMessage === true;
        },
        isDeletedRepliedToMessage() {
            return this.repliedToMessage.attributes.is_deleted === true;
        },
        isMissedCallMessage() {
            return this.message.attributes.is_missed_call === true;
        },
    },
    methods: {
        handleTwilioUserNameOf,
        scrollToLastMessage,
        ...mapActions('adDetails', ['getPhoneNumber', 'callPhoneNumber']),
        deleteMessage(message) {
            window.showConfirmation('', 'Are you sure you want to delete this message?', 'Delete', 'Cancel', 'warning', () => {
                const { attributes } = message;
                attributes.is_deleted = true;
                message.updateAttributes(attributes);
                this.isLocalDeletedMessage = true;
            });
        },
        retryMissedCall() {
            // TODO: if calling tracking will be needed per-user, the userUuid value needs to be adjusted
            // to the relatedUser not the messageUser
            this.callPhoneNumber({ userUuid: this.messageUser.attributes.uuid });
        },
    },
};
</script>
